import React from 'react';
import { View } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import TopPanel from '@components/operatorPanel/components/OperatorPanelInformation/TopPanel';
import SaveNoteButton from './SaveNoteButton';
import EmptyScreen from './EmptyScreen';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import CancelButton from './CancelButton';
import Actions from './Actions';
import NotesPreview from './NotesPreview';
import EditScreen from './EditScreen';
import OperatorPanelLoadingScreen from '@components/operatorPanel/components/OperatorPanelInformation/components/common/OperatorPanelLoadingScreen';

import { scaleOP } from '@components/frame';
import { ColorFunction } from '@styles/colors/types';
import { fontOP } from '@styles/fonts';
import { ICON_BUTTON_PADDING } from '@v2/pages/common/constants/base.constants';
import { setSelectedNoteId } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { NOTES_SCREEN_STATES, useManageOPNotes } from './hooks/useManageOPNotes';

interface OperatorPanelInformationNotesScreenProps {
  C: ColorFunction;
}

const OperatorPanelInformationNotesScreen: React.FC<OperatorPanelInformationNotesScreenProps> = ({ C }) => {
  const {
    screenState,
    setScreenState,
    setEditorValue,
    setTouched,
    newNoteLinks,
    setNewNoteLinks,
    memoizedNotes,
    isRenaming,
    activeNote,
    setIsRenaming,
    touched,
    cancelAlert,
    editorValue,
    onSave,
    editTitle,
    setEditTitle,
    notes,
    isNoteAdding,
  } = useManageOPNotes();

  const dispatch = useAppDispatch();

  const renderScreen = () => {
    switch (screenState) {
      case NOTES_SCREEN_STATES.LOADING:
        return <OperatorPanelLoadingScreen text={i18n.t('Loading notes')} />;

      case NOTES_SCREEN_STATES.EMPTY:
        return <EmptyScreen onPress={() => setScreenState(NOTES_SCREEN_STATES.CREATE)} />;

      case NOTES_SCREEN_STATES.EDIT:
      case NOTES_SCREEN_STATES.CREATE: {
        return <EditScreen setEditorValue={setEditorValue} setTouched={setTouched} links={newNoteLinks} setLinks={setNewNoteLinks} />;
      }
      case NOTES_SCREEN_STATES.PREVIEW:
        return <NotesPreview notes={memoizedNotes} />;
      default:
        return null;
    }
  };

  const panelContent = () => {
    switch (screenState) {
      case NOTES_SCREEN_STATES.LOADING:
        return null; // Don't show add button while loading

      case NOTES_SCREEN_STATES.EMPTY:
      case NOTES_SCREEN_STATES.PREVIEW:
        return (
          <TouchableElement onPress={() => setScreenState(NOTES_SCREEN_STATES.CREATE)}>
            {({ boxStyle, pressHoverColor }) => {
              return (
                <View
                  style={[
                    boxStyle,
                    {
                      backgroundColor: C(pressHoverColor('Button/Light/Only Text')),
                      padding: scaleOP(ICON_BUTTON_PADDING),
                      borderRadius: scaleOP(18),
                    },
                  ]}
                >
                  <Svg icon="plus" width={scaleOP(20)} height={scaleOP(20)} />
                </View>
              );
            }}
          </TouchableElement>
        );

      case NOTES_SCREEN_STATES.EDIT:
      case NOTES_SCREEN_STATES.CREATE:
        return (
          <>
            {isRenaming ? null : (
              <Actions
                documentId={activeNote?.documentId}
                documentFileName={activeNote?.fileName}
                removeNote={() => {
                  setEditorValue('');
                  setTouched(false);
                  setIsRenaming(false);
                  setEditTitle('');
                  setNewNoteLinks([]);
                  dispatch(setSelectedNoteId(null));
                  setScreenState(notes.length > 1 ? NOTES_SCREEN_STATES.PREVIEW : NOTES_SCREEN_STATES.EMPTY);
                }}
                onRename={() => {
                  setIsRenaming(true);
                }}
              />
            )}
            {!activeNote || touched || isRenaming ? (
              <CancelButton
                ref={cancelAlert}
                skipAlert={isRenaming || (!activeNote && !editorValue)}
                C={C}
                scale={scaleOP}
                font={fontOP}
                onDiscard={() => {
                  if (isRenaming) {
                    setEditTitle((activeNote?.fileNameOverride ?? activeNote?.title ?? activeNote?.title)?.replace?.('.rtf', '') ?? '');
                    setIsRenaming(false);

                    return;
                  }

                  setEditorValue('');
                  dispatch(setSelectedNoteId(null));
                  setScreenState(notes.length > 0 ? NOTES_SCREEN_STATES.PREVIEW : NOTES_SCREEN_STATES.EMPTY);
                  setTouched(false);
                  setIsRenaming(false);
                  setEditTitle('');
                }}
                onSave={onSave}
                updating={screenState === NOTES_SCREEN_STATES.EDIT}
              />
            ) : null}
            <SaveNoteButton onSubmit={onSave} isAdding={isNoteAdding} />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TopPanel
        title={
          isRenaming
            ? editTitle
            : (activeNote?.fileNameOverride ?? activeNote?.title ?? activeNote?.fileName)?.replace?.('.rtf', '') ?? i18n.t('Notes')
        }
        editing={isRenaming}
        setEditValue={setEditTitle}
        closeButton={![NOTES_SCREEN_STATES.EDIT, NOTES_SCREEN_STATES.CREATE].includes(screenState)}
        submit={onSave}
      >
        {panelContent()}
      </TopPanel>
      {renderScreen()}
    </>
  );
};

export default ColorWrapper(OperatorPanelInformationNotesScreen);
